/**
 * MaLoop — shared wrap-around for the site's scroll-snap sliders (PDP gallery + rail,
 * lightbox, reviews, ingredient carousel, product carousels, orbit).
 *
 *  MaLoop.jump(scroller, { left | top, host, text })
 *      Moves from one end to the other without rewinding through every slide: a short
 *      soft dissolve, an instant reposition, then a spring settle. Shows the loop cue.
 *  MaLoop.cue(host, text)
 *      A small frosted pill ("↻ 1 / 6") that drifts in and fades away, so visitors know
 *      the slider has come round to the start instead of scrolling on looking for more.
 *  MaLoop.edges(scroller, { next, prev })
 *      Touch swipes that push against either end call next()/prev(), so on the last slide
 *      the visitor can simply keep swiping. (Mouse drags are handled by each slider.)
 */
(() => {
  if (window.MaLoop) return;
  const reduce = window.matchMedia('(prefers-reduced-motion: reduce)');
  const SPRING = 'cubic-bezier(0.32, 0.72, 0, 1)';
  const ICON =
    '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M3 12a9 9 0 0 1 15.36-6.36L21 8"/><path d="M21 3v5h-5"/><path d="M21 12a9 9 0 0 1-15.36 6.36L3 16"/><path d="M3 21v-5h5"/></svg>';

  function cue(host, text) {
    if (!host) return;
    let el = host.querySelector(':scope > .ma-loop-cue');
    if (!el) {
      el = document.createElement('div');
      el.className = 'ma-loop-cue';
      el.setAttribute('aria-hidden', 'true');
      el.innerHTML = `${ICON}<span></span>`;
      if (getComputedStyle(host).position === 'static') host.style.position = 'relative';
      host.append(el);
    }
    el.querySelector('span').textContent = text || '';
    el.classList.remove('is-on');
    void el.offsetWidth; // restart the transition when looping twice in a row
    el.classList.add('is-on');
    clearTimeout(el._t);
    el._t = setTimeout(() => el.classList.remove('is-on'), 1500);
  }

  function jump(scroller, o) {
    const axis = o.top != null ? 'top' : 'left';
    const set = () => {
      const sb = scroller.style.scrollBehavior;
      scroller.style.scrollBehavior = 'auto';
      scroller.scrollTo({ [axis]: o[axis], behavior: 'instant' });
      scroller.style.scrollBehavior = sb;
    };
    cue(o.host, o.text);
    if (reduce.matches || !scroller.animate) return set();
    if (scroller._maJump) return;
    scroller._maJump = true;
    const out = scroller.animate(
      [{ opacity: 1, transform: 'none', filter: 'blur(0)' }, { opacity: 0, transform: 'scale(0.985)', filter: 'blur(2px)' }],
      { duration: 170, easing: 'cubic-bezier(0.4, 0, 1, 1)', fill: 'forwards' }
    );
    out.onfinish = () => {
      set();
      const back = scroller.animate(
        [{ opacity: 0, transform: 'scale(0.985)', filter: 'blur(2px)' }, { opacity: 1, transform: 'none', filter: 'blur(0)' }],
        { duration: 460, easing: SPRING }
      );
      out.cancel();
      back.onfinish = back.oncancel = () => (scroller._maJump = false);
    };
  }

  function edges(sc, h) {
    let x0 = 0, y0 = 0, atStart = false, atEnd = false;
    const max = () => sc.scrollWidth - sc.clientWidth;
    sc.addEventListener('touchstart', (e) => {
      const t = e.touches[0];
      x0 = t.clientX; y0 = t.clientY;
      atStart = sc.scrollLeft <= 2;
      atEnd = sc.scrollLeft >= max() - 2;
    }, { passive: true });
    sc.addEventListener('touchend', (e) => {
      if (!atStart && !atEnd) return;
      const t = e.changedTouches[0];
      const dx = t.clientX - x0, dy = t.clientY - y0;
      if (Math.abs(dx) < 40 || Math.abs(dx) < Math.abs(dy) * 1.2) return;
      if (dx < 0 && atEnd && sc.scrollLeft >= max() - 2) h.next();
      else if (dx > 0 && atStart && sc.scrollLeft <= 2) h.prev();
    }, { passive: true });
  }

  // The scrollLeft at which `el` rests when snapped, using the browser's own snap geometry
  // (scroll-padding, scroll-margin, snap-align). Aiming anywhere else leaves the slider a few px
  // past its snap point, so the next touch makes it jump. el.offsetLeft must be measured from the
  // scroller (the scroller is position: relative).
  function snapLeft(sc, el) {
    const cs = getComputedStyle(sc);
    const es = getComputedStyle(el);
    const pl = parseFloat(cs.scrollPaddingLeft) || 0;
    const pr = parseFloat(cs.scrollPaddingRight) || 0;
    const ml = parseFloat(es.scrollMarginLeft) || 0;
    const mr = parseFloat(es.scrollMarginRight) || 0;
    const align = es.scrollSnapAlign.split(' ').pop(); // "block inline" or one value for both
    let x = el.offsetLeft - ml - pl; // start (also used for none)
    if (align === 'center') x = el.offsetLeft + el.offsetWidth / 2 - (pl + (sc.clientWidth - pl - pr) / 2);
    else if (align === 'end') x = el.offsetLeft + el.offsetWidth + mr - (sc.clientWidth - pr);
    return Math.round(Math.min(sc.scrollWidth - sc.clientWidth, Math.max(0, x)));
  }

  window.MaLoop = { jump, cue, edges, snapLeft };

  // Plain CSS swipe rows (no JS of their own): swiping on past either end loops round.
  const ROWS = '.ma-before-after__grid, .ma-story-products__row, .ma-values--mobile-scroll .ma-values__grid';
  const bindRows = (root) =>
    root.querySelectorAll(ROWS).forEach((row) => {
      if (row._maLoop) return;
      row._maLoop = true;
      const n = () => row.children.length;
      const wrap = (dir) => {
        const max = row.scrollWidth - row.clientWidth;
        if (max > 2) jump(row, { left: dir > 0 ? 0 : max, host: row.parentElement, text: dir > 0 ? `1 / ${n()}` : `${n()} / ${n()}` });
      };
      edges(row, { next: () => wrap(1), prev: () => wrap(-1) });
    });
  document.readyState === 'loading' ? document.addEventListener('DOMContentLoaded', () => bindRows(document)) : bindRows(document);
  document.addEventListener('shopify:section:load', (e) => bindRows(e.target));
})();
